import { useEffect, useState } from "react";
import { Plus, PencilSimple, Trash, Calculator, Buildings, CurrencyInr, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { formatINR } from "../../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import PaymentModal, { PaymentStatusPill } from "../../components/PaymentModal";
import PurchaseAutoFetchModal from "../../components/PurchaseAutoFetchModal";

const today = () => new Date().toISOString().slice(0,10);
const thisMonth = () => new Date().toISOString().slice(0,7);
const empty = { date: today(), header: "", subhead: "", amount: 0, currency: "INR", fx_rate: 1, vendor: "", notes: "" };

export default function OperatingExpenses() {
  const { active, activeId } = useCompany();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [month, setMonth] = useState(thisMonth());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [paySummaries, setPaySummaries] = useState({});
  const [fetchOpen, setFetchOpen] = useState(false);
  const [autoFetchedBanner, setAutoFetchedBanner] = useState(null); // {vendor, bill_no, grand_total, items, created_new}

  const load = async () => {
    if (!activeId) { setList([]); setCats([]); return; }
    const [{ data: exps }, { data: cs }] = await Promise.all([
      api.get("/finance/operating-expenses", { params: { company_id: activeId, month } }),
      api.get("/finance/expense-categories", { params: { company_id: activeId } }),
    ]);
    setList(exps); setCats(cs);
    const summaries = {};
    await Promise.all((exps || []).map(async (e) => {
      try {
        const { data: s } = await api.get("/finance/payments", { params: { parent_type: "expense", parent_id: e.id } });
        summaries[e.id] = s;
      } catch { /* ignore */ }
    }));
    setPaySummaries(summaries);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId, month]);

  const openNew = () => {
    const firstHeader = cats[0]?.header || "Office Expenses";
    const firstSub = cats[0]?.subheads?.[0] || "Miscellaneous";
    setEditing(null); setForm({ ...empty, header: firstHeader, subhead: firstSub }); setAutoFetchedBanner(null); setOpen(true);
  };
  const openEdit = (o) => { setEditing(o); setForm({ ...empty, ...o }); setAutoFetchedBanner(null); setOpen(true); };

  const handleAutoFetched = (data) => {
    const p = data.expense_prefill || {};
    // Ensure header/subhead match an existing category if possible — otherwise keep AI's defaults
    let header = p.header || "Purchases";
    let subhead = p.subhead || "Vendor Bill";
    const matchCat = cats.find((c) => c.header.toLowerCase() === header.toLowerCase());
    if (!matchCat && cats[0]) {
      header = cats[0].header;
      subhead = cats[0].subheads?.[0] || subhead;
    } else if (matchCat) {
      header = matchCat.header;
      if (!matchCat.subheads?.some((s) => s.toLowerCase() === subhead.toLowerCase())) {
        subhead = matchCat.subheads?.[0] || subhead;
      }
    }
    setEditing(null);
    setForm({
      ...empty,
      date: p.date || today(),
      header, subhead,
      amount: p.amount || 0,
      currency: "INR",
      fx_rate: 1,
      vendor: p.vendor || "",
      notes: p.notes || "",
    });
    setAutoFetchedBanner({
      vendor: data.vendor?.name,
      bill_no: data.bill?.bill_no,
      grand_total: data.bill?.grand_total,
      items: (data.bill?.items || []).length,
      created_new: data.vendor?.created_new,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.header || !form.subhead) { toast.error("Pick header + sub-head"); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Amount required"); return; }
    const payload = { ...form, company_id: activeId, amount: parseFloat(form.amount || 0), fx_rate: parseFloat(form.fx_rate || 1) };
    if (editing) await api.put(`/finance/operating-expenses/${editing.id}`, payload);
    else await api.post("/finance/operating-expenses", payload);
    toast.success("Saved");
    setOpen(false); load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await api.delete(`/finance/operating-expenses/${id}`);
    toast.success("Deleted"); load();
  };

  if (!activeId) {
    return <Layout title="Operating Expenses"><div className="border border-black/10 p-16 text-center"><Buildings size={36} className="mx-auto text-[#D90429]"/><h3 className="font-display text-2xl font-bold tracking-tight mt-4">Pick a company first</h3><button onClick={()=>navigate("/finance/companies")} className="mt-5 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">Go to Companies</button></div></Layout>;
  }

  // Sub-heads for the currently selected header in dialog
  const subheadsForHeader = cats.find((c) => c.header === form.header)?.subheads || [];

  // Group list by header for display
  const byHeader = list.reduce((acc, o) => {
    const inr = (o.currency === "USD" ? Number(o.amount) * Number(o.fx_rate) : Number(o.amount)) || 0;
    if (!acc[o.header]) acc[o.header] = { items: [], total: 0 };
    acc[o.header].items.push({ ...o, inr });
    acc[o.header].total += inr;
    return acc;
  }, {});
  const grandTotal = Object.values(byHeader).reduce((s, b) => s + b.total, 0);

  return (
    <Layout
      title="Operating Expenses"
      subtitle={`${active?.name} · indirect / overhead spend not tied to a specific work order`}
      actions={
        <>
          <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="w-44 rounded-none" data-testid="opex-month-picker"/>
          <button onClick={()=>setFetchOpen(true)} data-testid="opex-auto-fetch-button" className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white"><Sparkle size={14} weight="bold"/> Auto-Fetch from Bill</button>
          <button onClick={openNew} data-testid="add-opex-button" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] active:scale-95"><Plus size={16}/> Add Expense</button>
        </>
      }
    >
      <div className="grid grid-cols-3 border border-black/10 bg-white mb-6">
        <div className="p-5 border-r border-black/5"><div className="overline">Total operating spend</div><div className="font-display text-3xl font-black tracking-tighter mt-2" data-testid="opex-total">{formatINR(grandTotal)}</div></div>
        <div className="p-5 border-r border-black/5"><div className="overline">Records</div><div className="font-display text-3xl font-black tracking-tighter mt-2">{list.length}</div></div>
        <div className="p-5"><div className="overline">Headers used</div><div className="font-display text-3xl font-black tracking-tighter mt-2">{Object.keys(byHeader).length}</div></div>
      </div>

      {list.length === 0 ? (
        <div className="border border-black/10 p-16 text-center text-[#717680]" data-testid="empty-opex">
          <Calculator size={36} className="mx-auto text-[#D90429]"/>
          <p className="mt-4">No operating expenses logged for {month}. Add electricity, WiFi, rent, petty cash etc.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {Object.entries(byHeader).map(([header, group])=>(
            <div key={header} className="border border-black/10 bg-white" data-testid={`opex-group-${header.replace(/\s+/g,'-').toLowerCase()}`}>
              <div className="flex items-center justify-between p-4 border-b border-black/10 bg-[#FFE5E9]/40">
                <div className="font-display text-xl font-bold tracking-tight">{header}</div>
                <div className="font-mono-shelv text-sm">{formatINR(group.total)} <span className="text-[#717680] text-xs">({group.items.length} entries)</span></div>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-[#F5F6F8] text-left text-xs"><tr>{["Date","Sub-head","Vendor","Amount","Notes",""].map(h=>(<th key={h} className="px-3 py-2 font-mono-shelv uppercase tracking-widest text-[#717680]">{h}</th>))}</tr></thead>
                <tbody>
                  {group.items.map((o)=>(
                    <tr key={o.id} className="border-t border-black/5 hover:bg-[#FAFBFC]" data-testid={`opex-row-${o.id}`}>
                      <td className="px-3 py-2 font-mono-shelv">{o.date}</td>
                      <td className="px-3 py-2"><span className="text-xs font-mono-shelv uppercase tracking-widest bg-[#D90429]/10 text-[#D90429] px-2 py-0.5">{o.subhead}</span></td>
                      <td className="px-3 py-2 text-[#4A4D57]">{o.vendor || "—"}</td>
                      <td className="px-3 py-2 font-mono-shelv">{o.currency==="USD"?`$${o.amount}`:formatINR(o.amount)} {o.currency==="USD" && <span className="text-[10px] text-[#717680]">@₹{o.fx_rate}</span>}</td>
                      <td className="px-3 py-2 text-[#4A4D57] truncate max-w-[12rem]">{o.notes || ""}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1 items-center">
                          {paySummaries[o.id] && <PaymentStatusPill status={paySummaries[o.id].status} />}
                          <button onClick={()=>setPayTarget(o)} className="p-1.5 hover:bg-[#0D9488]/10 text-[#0D9488]" title="Record payment" data-testid={`pay-opex-${o.id}`}><CurrencyInr size={13}/></button>
                          <button onClick={()=>openEdit(o)} className="p-1.5 hover:bg-black/5" data-testid={`edit-opex-${o.id}`}><PencilSimple size={13}/></button>
                          <button onClick={()=>remove(o.id)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`delete-opex-${o.id}`}><Trash size={13}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Edit operating expense" : "Add operating expense"}</DialogTitle></DialogHeader>
          {autoFetchedBanner && (
            <div className="border border-[#0D9488]/30 bg-[#0D9488]/5 px-3 py-2 text-xs flex flex-wrap items-center gap-3" data-testid="opex-autofetch-banner">
              <Sparkle size={14} weight="bold" className="text-[#0D9488]"/>
              <span className="font-semibold text-[#0A0A0A]">Auto-fetched from vendor bill</span>
              <span className="font-mono-shelv text-[#4A4D57]">Vendor: <b>{autoFetchedBanner.vendor}</b>{autoFetchedBanner.created_new && <span className="ml-1 text-[10px] px-1 py-0.5 bg-[#0D9488] text-white">NEW</span>}</span>
              {autoFetchedBanner.bill_no && <span className="font-mono-shelv text-[#4A4D57]">Bill #: <b>{autoFetchedBanner.bill_no}</b></span>}
              {autoFetchedBanner.grand_total > 0 && <span className="font-mono-shelv text-[#4A4D57]">Total: <b>₹{Number(autoFetchedBanner.grand_total).toLocaleString("en-IN")}</b></span>}
              {autoFetchedBanner.items > 0 && <span className="font-mono-shelv text-[#4A4D57]">{autoFetchedBanner.items} item(s)</span>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} className="rounded-none"/></div>
            <div><Label>Vendor (optional)</Label><Input value={form.vendor} onChange={(e)=>setForm({...form, vendor:e.target.value})} className="rounded-none"/></div>
            <div>
              <Label>Header *</Label>
              <Select value={form.header} onValueChange={(v)=>{ const subs = cats.find(c=>c.header===v)?.subheads || []; setForm({...form, header:v, subhead: subs[0] || ""}); }}>
                <SelectTrigger className="rounded-none" data-testid="opex-header-select"><SelectValue/></SelectTrigger>
                <SelectContent>{cats.map((c)=><SelectItem key={c.header} value={c.header}>{c.header}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sub-head *</Label>
              <Select value={form.subhead} onValueChange={(v)=>setForm({...form, subhead:v})}>
                <SelectTrigger className="rounded-none" data-testid="opex-subhead-select"><SelectValue/></SelectTrigger>
                <SelectContent>{subheadsForHeader.map((s)=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v)=>setForm({...form, currency:v, fx_rate: v==="INR"?1:form.fx_rate})}>
                <SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="INR">INR ₹</SelectItem><SelectItem value="USD">USD $</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Amount</Label><Input data-testid="opex-amount-input" type="number" value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})} className="rounded-none"/></div>
            {form.currency === "USD" && <div className="col-span-2"><Label>USD→INR rate</Label><Input type="number" value={form.fx_rate} onChange={(e)=>setForm({...form, fx_rate:e.target.value})} className="rounded-none"/></div>}
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} className="rounded-none"/></div>
          </div>
          <DialogFooter>
            <button onClick={()=>setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={save} data-testid="save-opex-button" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PaymentModal
        open={!!payTarget}
        onOpenChange={(o) => !o && setPayTarget(null)}
        parentType="expense"
        parentId={payTarget?.id}
        parentLabel={payTarget ? `${payTarget.header} · ${payTarget.subhead} · ${payTarget.date}` : ""}
        onUpdated={(s) => setPaySummaries((prev) => ({ ...prev, [payTarget.id]: s }))}
      />

      <PurchaseAutoFetchModal
        open={fetchOpen}
        onOpenChange={setFetchOpen}
        companyId={activeId}
        target="expense"
        onParsed={handleAutoFetched}
      />
    </Layout>
  );
}
